import React from 'react';
import { Upload, X, Film, Library } from 'lucide-react';
import { tintFromAccent, FieldError, FieldCaution } from '../steps/_shared';
import { validateAdSpecs, formatAdSpecViolations } from './validateAdSpecs';
import ProgressBar from './ProgressBar';

const ACCEPTED_TYPES = ['video/mp4'];
// CTV-625: 200MB, matching the selfservice-creatives bucket file_size_limit and
// the server-side upload-url cap. A 15/30s 1080p MP4 is well under this.
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB

interface CreativeUploaderProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  accentColor?: string;
  // CTV-457: previously-uploaded commercial. When set (and no `file` is
  // picked), the uploader renders the video in the same player UI it uses
  // for fresh File picks — no separate "already uploaded" banner. Caller
  // resolves the signed URL via /creatives/preview-url or /creatives/view-url.
  existingVideoUrl?: string | null;
  existingMeta?: { filename: string; sizeBytes: number } | null;
  // True while the caller is fetching `existingVideoUrl`. Renders a
  // loading shimmer in the player slot so the user doesn't see an empty
  // dropzone for a beat after returning to the step.
  resolvingExistingVideo?: boolean;
  // True while bytes are uploading to storage. Shows a progress bar across
  // the player area (wizard step 3 and Edit Commercial share this).
  uploading?: boolean;
  /** Real 0–100 byte progress while `uploading`. Omits % when unset. */
  uploadPercent?: number | null;
  // CTV-457 follow-up: when provided, an inline "Pick from library" button
  // appears inside the empty dropzone (between the "browse" line and the
  // format hint). Undefined hides the button entirely — first-time
  // advertisers see the unchanged upload-only dropzone.
  onPickFromLibrary?: () => void;
}

const CreativeUploader: React.FC<CreativeUploaderProps> = ({
  file,
  onFileChange,
  accentColor = '#3b82f6',
  existingVideoUrl = null,
  existingMeta = null,
  resolvingExistingVideo = false,
  uploading = false,
  uploadPercent = null,
  onPickFromLibrary,
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [validating, setValidating] = React.useState(false);
  const [audioWarning, setAudioWarning] = React.useState<string | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!file) { setFilePreviewUrl(null); return; }
    const url = URL.createObjectURL(file);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const validateAndSet = async (f: File) => {
    setError(null);
    setAudioWarning(null);
    if (!ACCEPTED_TYPES.includes(f.type)) {
      setError('Please upload an MP4 file.');
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setError('File must be under 200MB.');
      return;
    }
    setValidating(true);
    try {
      const result = await validateAdSpecs(f);
      if (!result.ok) {
        setError(formatAdSpecViolations(result.violations));
        return;
      }
      if (!result.audioDetected) {
        setAudioWarning(
          "Audio track not detected. If your commercial has audio, this is safe to ignore.",
        );
      }
      onFileChange(f);
    } finally {
      setValidating(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) void validateAndSet(droppedFile);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) void validateAndSet(selected);
    e.target.value = '';
  };

  const handleRemove = () => {
    setError(null);
    setAudioWarning(null);
    onFileChange(null);
  };

  // Three rendering paths share the same player chrome:
  //   1. File picked → blob URL
  //   2. Previously uploaded → caller-supplied signed URL
  //   3. Empty → dropzone
  const playerSource: { url: string; filename: string; sizeBytes: number } | null = file && filePreviewUrl
    ? { url: filePreviewUrl, filename: file.name, sizeBytes: file.size }
    : existingVideoUrl && existingMeta
      ? { url: existingVideoUrl, filename: existingMeta.filename, sizeBytes: existingMeta.sizeBytes }
      : null;

  if (playerSource) {
    return (
      <div className="space-y-3">
        <div
          className="relative rounded-2xl overflow-hidden bg-black"
          style={{ boxShadow: `0 12px 32px -18px rgba(15,23,42,0.25), 0 4px 12px -8px ${tintFromAccent(accentColor, 0.18)}` }}
        >
          <video src={playerSource.url} controls preload="metadata" className="w-full max-h-72 object-contain" />
          <button
            type="button"
            onClick={handleRemove}
            aria-label="Remove video"
            className="absolute top-2.5 right-2.5 bg-slate-900/70 hover:bg-slate-900 text-white rounded-full p-1.5 backdrop-blur-sm transition-colors"
            disabled={uploading}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {uploading && (
          <ProgressBar
            accentColor={accentColor}
            label="Uploading video…"
            percent={typeof uploadPercent === 'number' ? uploadPercent : 0}
            active
          />
        )}
        {!uploading && (
          <div className="flex items-center gap-2 px-1 text-[12px]">
            <Film className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-slate-700 font-medium truncate">{playerSource.filename}</span>
            {playerSource.sizeBytes > 0 && (
              <span className="text-slate-400">· {(playerSource.sizeBytes / (1024 * 1024)).toFixed(1)} MB</span>
            )}
          </div>
        )}
        {/* Aspect/duration violations early-return before the audio warning
            is set, so error and audioWarning never co-exist here — the
            blocking message wins by construction. */}
        <FieldCaution message={audioWarning ?? undefined} className="ml-1" />
      </div>
    );
  }

  // Either:
  //   • the caller is still fetching the signed URL (loading shimmer), or
  //   • the fetch resolved without a URL (preview unavailable). Either way
  //     we keep the player-shaped placeholder so the user knows a
  //     commercial is on file rather than seeing an empty dropzone.
  if (existingMeta && !file) {
    return (
      <div className="space-y-3">
        <div
          className="relative rounded-2xl overflow-hidden bg-black aspect-video flex items-center justify-center text-white/70"
          style={{ boxShadow: `0 12px 32px -18px rgba(15,23,42,0.25), 0 4px 12px -8px ${tintFromAccent(accentColor, 0.18)}` }}
        >
          {resolvingExistingVideo ? null : (
            <div className="flex flex-col items-center gap-1.5 text-center px-6">
              <Film className="w-6 h-6 text-white/40" />
              <div className="text-[12px] font-medium text-white/80">Preview unavailable</div>
              <div className="text-[10px] text-white/50">The video is still attached to this campaign.</div>
            </div>
          )}
          <button
            type="button"
            onClick={handleRemove}
            aria-label="Remove video"
            className="absolute top-2.5 right-2.5 bg-slate-900/70 hover:bg-slate-900 text-white rounded-full p-1.5 backdrop-blur-sm transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2 px-1 text-[12px]">
          <Film className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-700 font-medium truncate">{existingMeta.filename}</span>
          {existingMeta.sizeBytes > 0 && (
            <span className="text-slate-400">· {(existingMeta.sizeBytes / (1024 * 1024)).toFixed(1)} MB</span>
          )}
        </div>
      </div>
    );
  }

  const dragBg = isDragging ? tintFromAccent(accentColor, 0.06) : 'transparent';
  const dragBorder = isDragging ? accentColor : 'rgb(203, 213, 225)';

  return (
    <div>
      <div
        onDragOver={(e) => { if (validating) return; e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => { if (validating) { e.preventDefault(); return; } handleDrop(e); }}
        onClick={() => { if (!validating) inputRef.current?.click(); }}
        className={`rounded-2xl border-2 border-dashed p-8 md:p-12 text-center transition-all ${validating ? 'cursor-wait opacity-80' : 'cursor-pointer hover:border-slate-400'}`}
        style={{ borderColor: dragBorder, backgroundColor: dragBg }}
        aria-busy={validating}
      >
        {validating ? (
          <div className="-mx-8 md:-mx-12 px-0 py-2">
            <ProgressBar
              accentColor={accentColor}
              label="Checking your video…"
            />
          </div>
        ) : (
          <>
            <div
              className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center transition-colors"
              style={{
                backgroundColor: tintFromAccent(accentColor, isDragging ? 0.12 : 0.06),
                color: accentColor,
              }}
            >
              <Upload className="w-5 h-5" />
            </div>
            <p className="text-[14px] text-slate-700">
              Drag and drop your video, or{' '}
              <span className="font-semibold underline-offset-2 underline decoration-1" style={{ color: accentColor }}>
                browse
              </span>
            </p>
            {onPickFromLibrary && (
              <div className="mt-3 flex items-center justify-center">
                <button
                  type="button"
                  onClick={(e) => {
                    // Don't bubble into the dropzone's click-to-open-file handler.
                    e.stopPropagation();
                    onPickFromLibrary();
                  }}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold tracking-tight text-slate-700 hover:text-slate-900 bg-white border border-slate-300 hover:border-slate-500 rounded-full px-3.5 py-1.5 transition-colors"
                >
                  <Library className="w-3.5 h-3.5" />
                  Pick from library
                </button>
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-3 uppercase tracking-[0.08em] font-medium">
              MP4 · up to 200 MB
            </p>
            <p className="text-[11px] text-slate-500 mt-1">
              Must be a 15s or 30s ad.
            </p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,.mp4"
          onChange={handleInputChange}
          className="hidden"
        />
      </div>
      <FieldError message={error ?? undefined} className="ml-1" />
    </div>
  );
};

export default CreativeUploader;
